import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactPlayer from 'react-player';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import useRedirectToLogin from '../hooks/useRedirectToLogin';
import { useAddReminderMutation, useEpisodeAvailabilityMutation } from '../actions/apiSlice';
import { useLazySearchEpisodePromosQuery } from '../actions/searchTrailer';

const placeholderImageUrl = 'https://placehold.co/800x450.png?text=No+Image';

const EpisodeAvailability = ({ show, resetForm }) => {
  const redirectToLogin = useRedirectToLogin();
  const [searchParams] = useSearchParams();
  const tid = searchParams.get('tid');

  const [seasonNumber, setSeasonNumber] = useState('');
  const [episodeNumber, setEpisodeNumber] = useState('');
  const [availabilityMessage, setAvailabilityMessage] = useState('');
  const [maxEpisode, setMaxEpisode] = useState('');
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [isFirstClick, setIsFirstClick] = useState(true);
  const [videos, setVideos] = useState([]);
  const [view, setView] = useState('info'); // 'info' | 'promo'
  const [airDate, setAirDate] = useState('');
  const [responseData, setResponseData] = useState('');
  const [formattedAirDate, setFormattedAirDate] = useState('');

  const [triggersearchEpisodePromos, { data: trailerData, isLoading: trailerLoading, error: trailerError }] =
    useLazySearchEpisodePromosQuery();
  const [addSetReminder, { isSuccess: reminderSuccess, isError: isReminderError, error: reminderError }] =
    useAddReminderMutation();
  const [checkForEpisode, { isLoading: episodeLoad }] = useEpisodeAvailabilityMutation();

  const currentDate = new Date().getTime();
  const navigate = useNavigate();
  const hasShownReminderToast = useRef(false);

  useEffect(() => {
    if (trailerData) setVideos(trailerData.items);
  }, [trailerData]);

  useEffect(() => {
    if (resetForm) {
      setSeasonNumber('');
      setEpisodeNumber('');
      setResponseData('');
      setAvailabilityMessage('');
    }
    if (isReminderError) {
      if (
        reminderError?.data?.non_field_errors &&
        reminderError.data.non_field_errors.includes('A reminder for this episode already set!!')
      ) {
        navigate('/reminder');
      }
      toast.error(`${Object.values(reminderError.data)}`);
    }
    if (reminderSuccess && !hasShownReminderToast.current) {
      toast.success('Reminder set!', {
        onClick: () => navigate('/reminder'),
      });
      hasShownReminderToast.current = true;
    }
  }, [resetForm, reminderError, reminderSuccess, isReminderError, navigate, redirectToLogin]);

  if (!show || !show.title) return null;
  if (!show.kind.includes('series')) return null;

  const checkEpisodeAvailability = async (season, episode) => {
    const payload = {
      season,
      episode,
      title: show.title,
      kind: show.kind,
      title_id: show.imdbID,
      tmdb_id: tid,
    };
    try {
      const response = await checkForEpisode(payload).unwrap();
      const message = response.message;
      const airDateMs = new Date(response.airDate).getTime();

      setAvailabilityMessage(message);
      setAirDate(airDateMs);
      setFormattedAirDate(response.airDate);
      setResponseData(response);
      setMaxEpisode(response.extra_data?.total_episodes);
      setIsFirstClick(true);

      if (message.includes('not')) toast.warn(message);
      else toast.success(message);

      if (airDateMs >= currentDate) setShowReminderModal(true);
    } catch (err) {
      console.error('Failed to check episode availability:', err);
    }
  };

  const setReminder = async () => {
    const token = localStorage.getItem('aircheck_access');
    const formatDate = (date) => {
      const d = new Date(date);
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${d.getFullYear()}-${month}-${day}`;
    };

    if (!token) {
      toast.error('Please log in');
      redirectToLogin();
      return;
    }

    addSetReminder({
      set_reminder: true,
      air_date: formatDate(formattedAirDate),
      temp_data: show,
      season: seasonNumber,
      episode: episodeNumber,
    });
  };

  const handleCheckAvailability = (e) => {
    e.preventDefault();
    if (seasonNumber && episodeNumber) {
      checkEpisodeAvailability(seasonNumber, episodeNumber);
    } else {
      toast.error('Please enter both season and episode numbers.');
    }
  };

  const handlePromoLoad = () => {
    if (seasonNumber && episodeNumber && isFirstClick) {
      triggersearchEpisodePromos({
        query: show.title,
        seasonNumber,
        episodeNumber,
      });
      setIsFirstClick(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Check episode availability
      </Typography>

      <Box
        component="form"
        onSubmit={handleCheckAvailability}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' },
          gap: 1.5,
          mb: 2,
        }}
      >
        <TextField
          required
          type="number"
          label={`Season (1-${show.seasons || '?'})`}
          value={seasonNumber}
          inputProps={{ min: 1, max: show.seasons }}
          onChange={(e) => {
            const v = e.target.value;
            if (v < 1 || v > show.seasons) {
              e.target.setCustomValidity(`Season must be between 1 and ${show.seasons}`);
            } else {
              e.target.setCustomValidity('');
            }
            setSeasonNumber(v);
            setEpisodeNumber('');
            if (v) setMaxEpisode('');
          }}
        />
        <TextField
          required
          type="number"
          label={`Episode${maxEpisode ? ` (1-${maxEpisode})` : ''}`}
          value={episodeNumber}
          inputProps={{ min: 1, max: maxEpisode }}
          onChange={(e) => {
            const v = e.target.value;
            if (maxEpisode && (v < 1 || v > maxEpisode)) {
              e.target.setCustomValidity(`Episode must be between 1 and ${maxEpisode}`);
            } else {
              e.target.setCustomValidity('');
            }
            setEpisodeNumber(v);
          }}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={episodeLoad}
          sx={{ minWidth: 140 }}
        >
          {episodeLoad ? <CircularProgress size={20} color="inherit" /> : 'Check'}
        </Button>
      </Box>

      {availabilityMessage && airDate > currentDate && (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<NotificationsActiveIcon />}
          onClick={() => setShowReminderModal(true)}
          sx={{ mb: 2 }}
        >
          Set reminder
        </Button>
      )}

      {responseData && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <Chip
              icon={<InfoOutlinedIcon />}
              label="Episode info"
              clickable
              color={view === 'info' ? 'primary' : 'default'}
              onClick={() => setView('info')}
            />
            <Chip
              icon={<PlayCircleOutlineIcon />}
              label="Promo"
              clickable
              color={view === 'promo' ? 'primary' : 'default'}
              onClick={() => {
                setView('promo');
                handlePromoLoad();
              }}
            />
          </Stack>

          {view === 'info' ? (
            <Paper
              elevation={0}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
              }}
            >
              <Box
                component="img"
                src={
                  !responseData.image || responseData.image === ' '
                    ? placeholderImageUrl
                    : responseData.image
                }
                alt={responseData.titleText}
                sx={{
                  width: '100%',
                  display: 'block',
                  maxHeight: 480,
                  objectFit: 'cover',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.85) 100%)',
                  display: 'flex',
                  alignItems: 'flex-end',
                  p: 2.5,
                  color: '#fff',
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {responseData.titleText}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    {responseData.extra_data?.episode_rating && (
                      <Chip
                        size="small"
                        label={`★ ${responseData.extra_data.episode_rating}`}
                        sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }}
                      />
                    )}
                  </Stack>
                  {responseData.extra_data?.episode_plot && (
                    <Typography variant="body2" sx={{ opacity: 0.9, maxWidth: 800 }}>
                      {decodeURIComponent(responseData.extra_data.episode_plot)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>
          ) : (
            <Box>
              {trailerLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : videos && videos.length > 0 ? (
                videos.slice(0, 1).map((video) => (
                  <Box className="player-wrapper" key={video.id.videoId}>
                    <ReactPlayer
                      className="react-player"
                      url={`https://www.youtube.com/watch?v=${video.id.videoId}`}
                      width="100%"
                      height="100%"
                      controls
                    />
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary">
                  {trailerError ? `Error fetching trailer: ${trailerError.message}` : 'No promo available.'}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      <Dialog
        open={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Set reminder</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Set a reminder for{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {show.title}
            </Box>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Season {seasonNumber}, Episode {episodeNumber} · airs {formattedAirDate}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setShowReminderModal(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setShowReminderModal(false);
              setReminder();
            }}
          >
            Save reminder
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EpisodeAvailability;
